import { useEffect, useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, History } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { eur } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { Button } from "../ui/button";

export default function ImportView() {
  const { isAdmin } = useViewRole();
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [journal, setJournal] = useState([]);

  const loadJournal = () => api.get("/imports").then((r) => setJournal(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) loadJournal(); }, [isAdmin]);

  if (!isAdmin) {
    return <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-10 text-center text-slate-400">Section réservée à l'administrateur.</div>;
  }

  const analyze = async () => {
    if (!file) return toast.error("Sélectionnez un fichier .xlsx");
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      toast.success(`${data.nb_lignes} lignes analysées`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'analyse"); }
    finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/import/commit", { import_id: analysis.import_id });
      setResult(data); setAnalysis(null); setFile(null);
      loadJournal();
      toast.success("Import terminé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'import"); }
    finally { setLoading(false); }
  };

  const Stat = ({ label, value, color = "text-slate-100" }) => (
    <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className={`font-mono-metric text-lg font-bold ${color}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="import-view">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Importer un historique Excel</h1>
        <p className="text-slate-400 text-sm mt-1">Feuille « Suivi commercial » — import idempotent, sans doublon.</p>
      </div>

      {/* Étape 1 & 2 */}
      <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-5">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#0B0F17] border border-[#1E293B] cursor-pointer hover:border-amber-500/40 transition-colors">
            <FileSpreadsheet className="h-4 w-4 text-amber-400" />
            <span className="text-sm">{file ? file.name : "Choisir un fichier .xlsx"}</span>
            <input data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files[0]); setAnalysis(null); setResult(null); }} />
          </label>
          <Button data-testid="import-analyze-button" onClick={analyze} disabled={loading || !file} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
            <Upload className="h-4 w-4 mr-1" /> Analyser
          </Button>
        </div>
      </div>

      {/* Étape 3 : analyse + prévisualisation */}
      {analysis && (
        <div className="space-y-4" data-testid="import-analysis">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Lignes" value={analysis.nb_lignes} />
            <Stat label="Lignes ignorées (vides)" value={analysis.lignes_ignorees} color="text-slate-400" />
            <Stat label="Dossiers exploitables" value={analysis.dossiers_exploitables} />
            <Stat label="Nouveaux clients" value={analysis.nouveaux_clients} color="text-emerald-400" />
            <Stat label="Clients existants" value={analysis.clients_existants} color="text-sky-400" />
            <Stat label="Devis nouveaux" value={analysis.devis_nouveaux} color="text-emerald-400" />
            <Stat label="Devis déjà présents" value={analysis.devis_existants} color="text-amber-400" />
            <Stat label="Doublons potentiels" value={analysis.doublons_potentiels} color="text-amber-400" />
            <Stat label="N° réutilisés" value={analysis.numeros_reutilises} color="text-amber-400" />
            <Stat label="Doublons stricts" value={analysis.doublons_stricts} color="text-slate-300" />
            <Stat label="Anomalies" value={analysis.anomalies_count} color="text-red-400" />
          </div>

          <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
            <div className="px-5 py-3 border-b border-[#1E293B] text-sm font-semibold">Prévisualisation (Excel → CRM)</div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-[#1E293B]">
                    <th className="px-3 py-2">Client</th><th className="px-3 py-2">N° Devis</th><th className="px-3 py-2">Famille</th>
                    <th className="px-3 py-2">État → Statut</th><th className="px-3 py-2 text-right">Montant</th><th className="px-3 py-2 text-right">Marge hist.</th><th className="px-3 py-2">Vente</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.preview.map((p, i) => (
                    <tr key={i} className="border-b border-[#1E293B]/50">
                      <td className="px-3 py-2">{p.client || "—"}</td>
                      <td className="px-3 py-2 font-mono-metric text-amber-400">{p.numero || "—"}</td>
                      <td className="px-3 py-2">{p.famille || "—"}</td>
                      <td className="px-3 py-2">{p.etat} → <span className="text-slate-300">{p.statut}</span></td>
                      <td className="px-3 py-2 text-right font-mono-metric">{p.montant_total != null ? eur(p.montant_total) : "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-metric text-emerald-400">{p.historical_margin != null ? eur(p.historical_margin) : "—"}</td>
                      <td className="px-3 py-2">{p.type_vente || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {analysis.anomalies_count > 0 && (
            <div className="rounded-xl bg-red-500/5 border border-red-500/20 p-4">
              <div className="flex items-center gap-2 text-red-400 text-sm font-semibold mb-2"><AlertTriangle className="h-4 w-4" /> {analysis.anomalies_count} anomalies détectées</div>
              <div className="max-h-40 overflow-y-auto text-xs text-slate-400 space-y-1">
                {analysis.anomalies.slice(0, 30).map((a, i) => (
                  <div key={i}>Ligne {a.ligne} — {a.client || "?"} ({a.numero || "sans n°"}) : {a.problemes.join(", ")}</div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button data-testid="import-commit-button" onClick={commit} disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-[#0B0F17] font-semibold">
              VALIDER L'IMPORT <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Étape 5 : résultat + rapprochement */}
      {result && (
        <div className="space-y-4" data-testid="import-result">
          <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4 flex items-center gap-2 text-emerald-400 text-sm font-semibold">
            <CheckCircle2 className="h-5 w-5" /> Import terminé — {result.counts.dossiers_importes} dossiers, {result.counts.clients_crees} clients créés, {result.counts.projets_crees} projets, {result.counts.doublons_evites} doublons évités
          </div>
          <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden">
            <div className="px-5 py-3 border-b border-[#1E293B] text-sm font-semibold">Rapprochement Excel ↔ CRM</div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-[#1E293B]"><th className="px-4 py-2">Champ</th><th className="px-3 py-2 text-right">Excel</th><th className="px-3 py-2 text-right">CRM</th><th className="px-3 py-2 text-right">Écart</th></tr></thead>
              <tbody>
                {result.reconciliation.map((r) => (
                  <tr key={r.champ} className="border-b border-[#1E293B]/50">
                    <td className="px-4 py-2">{r.champ}</td>
                    <td className="px-3 py-2 text-right font-mono-metric">{r.excel}</td>
                    <td className="px-3 py-2 text-right font-mono-metric">{r.crm}</td>
                    <td className={`px-3 py-2 text-right font-mono-metric ${Math.abs(r.ecart) < 0.01 ? "text-emerald-400" : "text-red-400"}`}>{r.ecart}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Journal d'import */}
      <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden" data-testid="import-journal">
        <div className="px-5 py-3 border-b border-[#1E293B] text-sm font-semibold flex items-center gap-2"><History className="h-4 w-4 text-slate-400" /> Historique des imports</div>
        {journal.length === 0 ? (
          <div className="p-5 text-sm text-slate-500">Aucun import pour l'instant.</div>
        ) : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-slate-500 border-b border-[#1E293B]"><th className="px-4 py-2">Date</th><th className="px-3 py-2">Fichier</th><th className="px-3 py-2 text-right">Lignes</th><th className="px-3 py-2 text-right">Créés</th><th className="px-3 py-2 text-right">Doublons</th><th className="px-3 py-2 text-right">Anomalies</th></tr></thead>
            <tbody>
              {journal.map((j) => (
                <tr key={j.id} className="border-b border-[#1E293B]/50">
                  <td className="px-4 py-2">{(j.date || "").slice(0, 10)}</td>
                  <td className="px-3 py-2">{j.filename}</td>
                  <td className="px-3 py-2 text-right font-mono-metric">{j.nb_lignes}</td>
                  <td className="px-3 py-2 text-right font-mono-metric text-emerald-400">{j.creations}</td>
                  <td className="px-3 py-2 text-right font-mono-metric text-amber-400">{j.doublons}</td>
                  <td className="px-3 py-2 text-right font-mono-metric text-red-400">{j.anomalies}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
